// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { Stair } from '../../../api/types';
import { deriveStair, stairProfile } from '../../../bygning/stairs';
import styles from './StairProfile.module.css';

export interface StairProfileProps {
  stair: Stair;
  /** "Stueplan", "1. sal" (null: arrives nowhere). */
  from: string;
  to: string | null;
  /** The solids no longer follow the model (`solids_stale`). */
  stale: boolean;
  rederiving: boolean;
  canEdit: boolean;
  onRederive: () => void;
}

const W = 260;
const H = 120;
const PAD = 6;

const level = (z: number) => `${z >= 0 ? '+' : '−'}${Math.abs(z).toFixed(2).replace('.', ',')}`;

/**
 * Egenskaber's stair preview (mock 06): the stair from the side, its
 * floors as dashed level lines, what it breaks of the guidance (2s + g,
 * riser, going, width) in Danish, and — when it cuts the floor above —
 * whether the hole is built yet, with Genberegn.
 */
export function StairProfile({ stair, from, to, stale, rederiving, canEdit, onRederive }: StairProfileProps) {
  const d = deriveStair(stair);
  const p = stairProfile(stair);
  const run = Math.max(p.points[p.points.length - 1]?.[0] ?? 1, 0.1);
  const k = Math.min((W - 2 * PAD) / run, (H - 2 * PAD - 14) / Math.max(p.rise, 0.1));
  const x0 = W - PAD - run * k;
  const yBase = H - PAD - 8;
  const pts = p.points.map(([x, z]) => `${(x0 + x * k).toFixed(1)},${(yBase - z * k).toFixed(1)}`).join(' ');
  const yTop = yBase - p.rise * k;
  const holeWaits = stair.cut_slab_above && stair.storey_to_id !== null && (!stair.built || stale);
  return (
    <div className={styles.profile}>
      <svg className={styles.svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Trappen set fra siden: ${d.riser_count} trin fra ${from} til ${to ?? 'ingen etage'}`}>
        <line x1={0} y1={yTop} x2={W} y2={yTop} className={styles.level} />
        <line x1={0} y1={yBase} x2={W} y2={yBase} className={styles.level} />
        <text x={W - PAD} y={yTop - 4} textAnchor="end" className={styles.levelLabel}>
          {to ?? 'Top'} {level(stair.z_top)}
        </text>
        <text x={PAD} y={yBase + 10} className={styles.levelLabel}>
          {from} {level(stair.z_base)}
        </text>
        <polyline points={pts} className={styles.steps} />
      </svg>
      {d.advice.length > 0 ? (
        <ul className={styles.advice} aria-label="Råd (BR18 vejledning)">
          {d.advice.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      ) : (
        <p className={styles.ok}>Stigning, grund, 2s + g og bredde ligger inden for BR18-vejledningen.</p>
      )}
      {holeWaits && (
        <p className={styles.hole}>
          <span>{rederiving ? `Hullet i ${to} genberegnes…` : `Hullet i ${to} kommer med Genberegn`}</span>
          <button type="button" className={styles.link} disabled={!canEdit || rederiving} onClick={onRederive}>
            Genberegn
          </button>
        </p>
      )}
    </div>
  );
}
